import { useEffect, useState } from 'react';
import { listingsApi, type AIRecommendation, type ListingHealth, type MarketplaceListing } from '../../api/listings';

export function ListingHealthDashboardPage() {
  const [listings, setListings] = useState<MarketplaceListing[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    void listingsApi.list().then(setListings).catch((e) => setError(String(e)));
  }, []);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Listing Health Dashboard</h1>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {listings.map((l) => (
          <div key={l.id} className="rounded-xl border bg-white p-4">
            <p className="font-medium truncate">{l.content.title}</p>
            <p className="text-xs text-slate-500">{l.marketplace}</p>
            <p className="mt-2 text-2xl font-semibold">{l.health?.score?.toFixed(0) ?? '—'}</p>
            <p className="text-xs text-slate-500">Completeness {l.health?.completeness?.toFixed(0)}%</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SEODashboardPage() {
  const [listingId, setListingId] = useState('');
  const [health, setHealth] = useState<ListingHealth | null>(null);
  const [recs, setRecs] = useState<AIRecommendation[]>([]);

  const onAnalyze = async () => {
    if (!listingId) return;
    const [h, r] = await Promise.all([listingsApi.health(listingId), listingsApi.recommendations(listingId)]);
    setHealth(h);
    setRecs(r.filter((x) => x.type.includes('seo')));
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">SEO Dashboard</h1>
      <div className="mt-6 flex gap-3">
        <input className="rounded border px-3 py-2 text-sm" placeholder="Listing ID" value={listingId} onChange={(e) => setListingId(e.target.value)} />
        <button type="button" className="rounded border px-4 py-2 text-sm" onClick={() => void onAnalyze()}>Analyze</button>
      </div>
      {health && (
        <div className="mt-6 rounded-xl border bg-white p-4">
          <p>SEO Score: <strong>{health.seo_score}</strong></p>
          <ul className="mt-4 space-y-2 text-sm">
            {recs.map((r, i) => <li key={i}>{r.reason}: {r.suggested}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

export function ComplianceDashboardPage() {
  const [listings, setListings] = useState<MarketplaceListing[]>([]);

  useEffect(() => {
    void listingsApi.list().then(setListings);
  }, []);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Compliance Dashboard</h1>
      <table className="mt-6 w-full text-left text-sm">
        <thead><tr className="border-b"><th className="py-2">Listing</th><th>Marketplace</th><th>Compliance</th><th>Missing</th></tr></thead>
        <tbody>
          {listings.map((l) => (
            <tr key={l.id} className="border-b">
              <td className="py-2">{l.content.title}</td>
              <td>{l.marketplace}</td>
              <td>{l.health?.compliance_score?.toFixed(0)}</td>
              <td>{l.health?.missing_images ? 'Images' : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function BulkListingGeneratorPage() {
  const [productIds, setProductIds] = useState('');
  const [marketplace, setMarketplace] = useState<'amazon_in'>('amazon_in');
  const [result, setResult] = useState<MarketplaceListing[]>([]);
  const [error, setError] = useState('');

  const onGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const ids = productIds.split(',').map((s) => s.trim()).filter(Boolean);
      const items = await listingsApi.bulkGenerate({ product_ids: ids, marketplace });
      setResult(items);
    } catch (err) {
      setError(String(err));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Bulk Listing Generator</h1>
      <form onSubmit={onGenerate} className="mt-6 space-y-3 rounded-xl border bg-white p-4">
        <input className="w-full rounded border px-3 py-2 text-sm" placeholder="Product IDs (comma-separated)" value={productIds} onChange={(e) => setProductIds(e.target.value)} />
        <select className="rounded border px-3 py-2 text-sm" value={marketplace} onChange={(e) => setMarketplace(e.target.value as 'amazon_in')}>
          <option value="amazon_in">Amazon India</option>
          <option value="flipkart">Flipkart</option>
        </select>
        <button type="submit" className="rounded bg-slate-900 px-4 py-2 text-sm text-white">Generate</button>
      </form>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <p className="mt-4 text-sm">Generated: {result.length} listings</p>
    </div>
  );
}

export function VersionComparisonPage() {
  const [idA, setIdA] = useState('');
  const [idB, setIdB] = useState('');
  const [diff, setDiff] = useState<Array<{ field: string; value_a: string; value_b: string; changed: boolean }>>([]);

  const onCompare = async () => {
    const cmp = await listingsApi.compare(idA, idB);
    setDiff(cmp.differences);
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Version Comparison</h1>
      <div className="mt-6 flex flex-wrap gap-3">
        <input className="rounded border px-3 py-2 text-sm" placeholder="Listing A" value={idA} onChange={(e) => setIdA(e.target.value)} />
        <input className="rounded border px-3 py-2 text-sm" placeholder="Listing B" value={idB} onChange={(e) => setIdB(e.target.value)} />
        <button type="button" className="rounded border px-4 py-2 text-sm" onClick={() => void onCompare()}>Compare</button>
      </div>
      <ul className="mt-6 space-y-2 text-sm">
        {diff.map((d) => (
          <li key={d.field} className={`rounded border p-3 ${d.changed ? 'border-amber-300 bg-amber-50' : 'bg-white'}`}>
            <strong>{d.field}</strong>: {d.value_a} → {d.value_b}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function MarketplaceTemplatesPage() {
  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Marketplace Templates</h1>
      <p className="mt-2 text-sm text-slate-600">Listing templates inherit product content and apply marketplace-specific overrides for Amazon, Flipkart, Myntra, AJIO, Shopify, and WooCommerce.</p>
    </div>
  );
}

export function AIRecommendationsPanelPage() {
  const [listingId, setListingId] = useState('');
  const [recs, setRecs] = useState<AIRecommendation[]>([]);

  const onLoad = async () => {
    if (!listingId) return;
    setRecs(await listingsApi.recommendations(listingId));
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">AI Recommendation Panel</h1>
      <div className="mt-6 flex gap-3">
        <input className="rounded border px-3 py-2 text-sm" placeholder="Listing ID" value={listingId} onChange={(e) => setListingId(e.target.value)} />
        <button type="button" className="rounded border px-4 py-2 text-sm" onClick={() => void onLoad()}>Get recommendations</button>
      </div>
      <ul className="mt-6 space-y-3">
        {recs.map((r, i) => (
          <li key={i} className="rounded-xl border bg-white p-4">
            <p className="font-medium">{r.type}</p>
            <p className="text-sm text-slate-600">{r.reason}</p>
            <p className="mt-2 text-sm"><strong>Suggested:</strong> {r.suggested}</p>
            <p className="text-xs text-slate-400">Confidence: {(r.confidence * 100).toFixed(0)}%</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
